import { Icon } from '@chakra-ui/icon';
import { Box, Flex, HStack, Text } from '@chakra-ui/layout';
import { Link } from '@chakra-ui/react';
import { CopyToClipboard } from '@duke-hq/ui/components/CopyToClipboard/CopyToClipboard';
import { djs } from '@duke-hq/djs';
import { ArrowUpRight, BadgeCheck, Landmark, Mail, MapPin, Phone } from 'lucide-react';
import { ContractTypes } from '@duke-hq/shared-constants/enrolments';
import { CourseStatusSchema, type CourseStatus } from '@duke-hq/shared-constants/courseStatus';
import SupersededCourseStatusBadge from '../SupersededCourseStatusBadge';
import { Link as RouterLink } from 'react-router-dom';

export interface EnrolmentMetadataLabels {
  accredited: string;
  consultant: string;
  segment: string;
  pace: string;
  fullTime: string;
  partTime: string;
  startDate: string;
  firstActivity: string;
  targetDate: string;
  contract: string;
  placements: string;
}

const defaultLabels: EnrolmentMetadataLabels = {
  accredited: 'Accredited',
  consultant: 'Consultant:',
  segment: 'Segment',
  pace: 'Pace',
  fullTime: 'Full-time',
  partTime: 'Part-time',
  startDate: 'Start date',
  firstActivity: 'First activity',
  targetDate: 'Target date',
  contract: 'Contract',
  placements: 'View placements',
};

type Props = {
  course: {
    title: string;
    identifier: string | null;
    accredited: boolean;
    status: CourseStatus;
  } | null;
  tenantName?: string;
  enrolmentMetrics: {
    firstActivity: Date | null;
    targetDate: { medium: string } | null;
  };
  enrolment: {
    course_segment: string;
    work_schedule: string;
    start_at_date: Date;
    enrolment_contract_type: string | null;
    consultant?: {
      first_name: string;
      last_name: string | null;
      siteAccess?: { siteLocations: { name: string; state: string } | null }[] | null;
    } | null;
  };
  isSkillSet: boolean;
  labels?: Partial<EnrolmentMetadataLabels>;
  consultantHref?: string;
  consultantEmail?: string;
  consultantPhone?: string;
  placementHref?: string;
};

function InfoField({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | number | null | undefined;
  testId?: string;
}): React.ReactElement | null {
  if (value === null || value === undefined) {
    return null;
  }

  return (
    <HStack gap={1} data-testid={testId}>
      <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
        {label}:
      </Text>
      <Text fontSize="sm" whiteSpace="nowrap">
        {value}
      </Text>
    </HStack>
  );
}

function EnrolmentMetadata({
  course,
  enrolment,
  enrolmentMetrics,
  tenantName,
  isSkillSet,
  labels,
  consultantHref,
  consultantEmail,
  consultantPhone,
  placementHref,
}: Props): React.ReactElement {
  const l = { ...defaultLabels, ...labels };

  return (
    <Box w="full">
      {course !== null ? (
        <Flex alignItems="center" columnGap="6px" wrap="wrap">
          <Text fontSize="xl" fontWeight="semibold" lineHeight="7">
            {isSkillSet ? `Skill set:  ${course.title}` : course.title}
          </Text>

          {course.identifier != null || course.identifier !== '' ? (
            <Text fontSize="sm" fontWeight="medium" color="bg.inverse-emphasis" lineHeight="5">
              {course.identifier}
            </Text>
          ) : null}

          {course.status === CourseStatusSchema.enum.SUPERSEDED ? (
            <Flex alignItems="center" gap={1}>
              <SupersededCourseStatusBadge status={course.status} />
            </Flex>
          ) : null}

          {course.accredited ? (
            <Flex alignItems="center" gap={1}>
              <Text fontSize="sm" fontWeight="semibold" color="teal.500">
                {l.accredited}
              </Text>
              <Icon as={BadgeCheck} color="teal.500" fill="teal.500" stroke="fg.inverse" />
            </Flex>
          ) : null}

          {placementHref != null ? (
            <Link
              as={RouterLink}
              to={placementHref}
              fontSize="sm"
              fontWeight="semibold"
              whiteSpace="nowrap"
            >
              <HStack gap={1} alignItems="center">
                <Text as="span">{l.placements}</Text>
                <Icon as={ArrowUpRight} fontSize="sm" />
              </HStack>
            </Link>
          ) : null}
        </Flex>
      ) : null}

      {/* Consultant & contact details */}

      <HStack flexWrap="wrap" rowGap={1} columnGap={2} my={1} alignItems="center">
        {tenantName !== undefined ? (
          <HStack gap={1} alignItems="center">
            <Icon as={Landmark} color="fg.emphasis" fontSize="sm" />
            <Text fontSize="sm" fontWeight="medium" color="bg.inverse-emphasis" lineHeight="5">
              {tenantName}
            </Text>
          </HStack>
        ) : null}

        {enrolment.consultant ? (
          <>
            <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
              {l.consultant}
            </Text>

            {consultantHref != null ? (
              <Link as={RouterLink} to={consultantHref} fontSize="sm" whiteSpace="nowrap">
                {enrolment.consultant.first_name} {enrolment.consultant.last_name}
              </Link>
            ) : (
              <Text fontSize="sm" whiteSpace="nowrap">
                {enrolment.consultant.first_name} {enrolment.consultant.last_name}
              </Text>
            )}

            {consultantEmail != null ? (
              <HStack gap={1} alignItems="center">
                <Icon as={Mail} color="fg.emphasis" fontSize="sm" />
                <CopyToClipboard
                  value={consultantEmail}
                  label={`Copy email: ${consultantEmail}`}
                  revealOnHover
                >
                  <Link href={`mailto:${consultantEmail}`} fontSize="sm" whiteSpace="nowrap">
                    {consultantEmail}
                  </Link>
                </CopyToClipboard>
              </HStack>
            ) : null}

            {consultantPhone != null ? (
              <HStack gap={1} alignItems="center">
                <Icon
                  aria-label={`Phone: ${consultantPhone}`}
                  as={Phone}
                  color="fg.emphasis"
                  fontSize="sm"
                />
                <CopyToClipboard
                  value={consultantPhone}
                  label={`Copy phone: ${consultantPhone}`}
                  revealOnHover
                >
                  <Link href={`tel:${consultantPhone}`} fontSize="sm" whiteSpace="nowrap">
                    {consultantPhone}
                  </Link>
                </CopyToClipboard>
              </HStack>
            ) : null}

            {enrolment.consultant?.siteAccess != null &&
            enrolment.consultant?.siteAccess[0]?.siteLocations ? (
              <HStack gap={1} alignItems="center">
                <Icon as={MapPin} color="fg.emphasis" fontSize="sm" />
                <Text fontSize="sm" whiteSpace="nowrap">
                  {enrolment.consultant.siteAccess[0].siteLocations.name}
                  {', '}
                  {enrolment.consultant.siteAccess[0].siteLocations.state}
                </Text>
              </HStack>
            ) : null}
          </>
        ) : null}
      </HStack>

      {/* Enrolment details */}
      <HStack flexWrap="wrap" rowGap={1} columnGap={2}>
        <InfoField label={l.segment} value={enrolment.course_segment} />
        <InfoField
          label={l.pace}
          value={enrolment.work_schedule === 'FULL_TIME' ? l.fullTime : l.partTime}
        />
        <InfoField label={l.startDate} value={djs(enrolment.start_at_date).format('D MMM YY')} />
        <InfoField
          label={l.firstActivity}
          value={
            enrolmentMetrics.firstActivity
              ? djs(enrolmentMetrics.firstActivity).format('D MMM YY')
              : 'N/A'
          }
        />
        <InfoField label={l.targetDate} value={enrolmentMetrics.targetDate?.medium ?? 'N/A'} />
        <InfoField
          label={l.contract}
          value={
            ContractTypes.find((c) => c.value === enrolment.enrolment_contract_type)?.label ??
            enrolment.enrolment_contract_type
          }
        />
      </HStack>
    </Box>
  );
}

export default EnrolmentMetadata;
